// 키 배치 프리셋(레인 C, docs/PC-WINDOWS-PRD.md 4절). 대표님 2026-10-28 "PC로 이용할 때 K를 누르면 스킬창, 스탯 창도".
// 기존 전투 키는 K = 회피, C = 카메라 초기화라 K·C 창 단축키와 겹친다. 그래서 두 배치를 둔다(지휘 결정 2026-10-28):
//   action(액션식, 기존) — J 공격 · K/Shift 회피 · C 카메라 초기화 · N 스킬 창.
//   rpg(PC RPG식)        — J 공격 · Shift 회피 · K 스킬 창 · C 스탯 창 · Home 카메라 초기화(N도 스킬 창).
// 새 프로필의 기본은 rpg, 기존 진행이 있는 기기는 지금 배치(action)를 유지하고 첫 접속 때 가장자리 안내를 한 번 보인다.
// 순수 판정 + 기기 전용 저장(dungeonhero.keys.v1, 동기화하지 않음). 프레임 루프에서 부르지 않는다(키 이벤트 단위).

export type KeyPreset = 'action' | 'rpg';
export const KEY_PRESETS: readonly KeyPreset[] = ['rpg', 'action'];
export const KEY_PRESET_NAME: Readonly<Record<KeyPreset, string>> = { rpg: 'PC RPG식', action: '액션식' };
export const KEY_PRESET_DESC: Readonly<Record<KeyPreset, string>> = {
  rpg: 'K 스킬 창 · C 스탯 창 · Shift 회피 · Home 카메라 초기화',
  action: 'K·Shift 회피 · C 카메라 초기화 · N 스킬 창',
};

/** 기기 전용 키 배치 저장 */
export const KEYMAP_KEY = 'dungeonhero.keys.v1';

/** 지금 배치에서 회피 키인가(Shift는 두 배치 모두) */
export function isDodgeCode(code: string, p: KeyPreset): boolean {
  return code === 'ShiftLeft' || code === 'ShiftRight' || (p === 'action' && code === 'KeyK');
}
/** 카메라 초기화 키(Home은 두 배치 모두 — 안내 문구가 바뀌어도 손이 기억하는 키가 남게) */
export function isCameraResetCode(code: string, p: KeyPreset): boolean {
  return code === 'Home' || (p === 'action' && code === 'KeyC');
}
/** 화면에 적는 회피 키 / 카메라 초기화 키(배치별) */
export function dodgeKeyLabel(p: KeyPreset): string { return p === 'action' ? 'K' : 'SHIFT'; }
export function cameraResetKeyLabel(p: KeyPreset): string { return p === 'action' ? 'C' : 'Home'; }
/** 창 열기 단축키(배치별). quest G · map M · skills N은 두 배치 모두 */
export type WindowKeyAction = 'quest' | 'skills' | 'map' | 'stats';
export function windowKeyOf(code: string, p: KeyPreset): WindowKeyAction | null {
  switch (code) {
    case 'KeyG': return 'quest';
    case 'KeyM': return 'map';
    case 'KeyN': return 'skills';
    case 'KeyK': return p === 'rpg' ? 'skills' : null;
    case 'KeyC': return p === 'rpg' ? 'stats' : null;
    default: return null;
  }
}

/** 저장된 설정 */
export interface KeymapPrefs { readonly preset: KeyPreset; /** 기존 사용자 안내(새 배치 소개)를 봤는가 */ readonly hinted: boolean }

/** 진행 기록이 있는 기기인가(기존 사용자 판정 — 성장·저장 키 중 하나라도 있으면) */
// account.v1·account.apply.v1: 계정 로그인으로 진행을 받을 기기(진행 키가 아직 비어 있어도 기존 사용자 손을 바꾸지 않는다)
export const PROGRESS_PROBE_KEYS: readonly string[] = ['dungeonhero.save.v1', 'dungeonhero.growth.v2', 'dungeonhero.growth.v1', 'dungeonhero.profile.v2', 'dungeonhero.characters.v1', 'dungeonhero.account.v1', 'dungeonhero.account.apply.v1'];

export interface KeyArea { getItem(key: string): string | null; setItem(key: string, value: string): void }

/**
 * 읽기: 저장이 있으면 그대로. 없으면 진행 기록이 있는 기기 = action(+안내 대기), 새 기기 = rpg.
 * 저장소를 못 읽으면 action(지금까지와 같은 손 — 안전한 쪽)
 */
export function loadKeymap(area: KeyArea | null): KeymapPrefs {
  if (!area) return { preset: 'action', hinted: true };
  try {
    const raw = area.getItem(KEYMAP_KEY);
    if (raw) {
      let j: Partial<KeymapPrefs> | null = null;
      try { j = JSON.parse(raw) as Partial<KeymapPrefs>; } catch { j = null; } // 손상: 아래 진행 판정으로
      if (j && (j.preset === 'action' || j.preset === 'rpg')) return { preset: j.preset, hinted: j.hinted === true };
    }
    const existing = PROGRESS_PROBE_KEYS.some((k) => area.getItem(k) !== null);
    return existing ? { preset: 'action', hinted: false } : { preset: 'rpg', hinted: true };
  } catch {
    return { preset: 'action', hinted: true };
  }
}

export function saveKeymap(area: KeyArea | null, p: KeymapPrefs): void {
  try { area?.setItem(KEYMAP_KEY, JSON.stringify({ preset: p.preset, hinted: p.hinted })); } catch { /* 이번 실행 동안만 유지 */ }
}

/**
 * 실행 중 공유 상태(입력·카메라·단축키·도움말이 같은 값을 읽는다). 설정에서 바꾸면 set()이 저장하고 듣는 쪽에 알린다.
 * 브라우저 밖(시험)에서는 저장소 없이 action으로 시작한다.
 */
class KeymapState {
  private cur: KeymapPrefs;
  private readonly subs = new Set<(p: KeyPreset) => void>();
  constructor() {
    let area: KeyArea | null = null;
    try { area = typeof localStorage !== 'undefined' ? localStorage : null; } catch { area = null; }
    this.cur = loadKeymap(area);
    // 새 기기의 기본값(rpg)은 처음 읽을 때 저장해 둔다 — 나중에 진행이 생겨도 배치가 바뀌지 않게
    try { if (area && area.getItem(KEYMAP_KEY) === null && this.cur.hinted) saveKeymap(area, this.cur); } catch { /* 저장 못 해도 이번 실행은 그대로 */ }
    // 다른 탭에서 배치를 바꾸면 이 탭도 따라간다
    try {
      if (area && typeof window !== 'undefined') window.addEventListener('storage', (e) => {
        if (e.key !== KEYMAP_KEY) return;
        const next = loadKeymap(area);
        if (next.preset === this.cur.preset && next.hinted === this.cur.hinted) return;
        const changed = next.preset !== this.cur.preset;
        this.cur = next;
        if (changed) for (const f of this.subs) f(next.preset);
      });
    } catch { /* 이벤트 없는 환경 */ }
  }
  get preset(): KeyPreset { return this.cur.preset; }
  get hinted(): boolean { return this.cur.hinted; }
  set(preset: KeyPreset): void {
    this.cur = { preset, hinted: true };
    this.persist();
    for (const f of this.subs) f(preset);
  }
  markHinted(): void { if (!this.cur.hinted) { this.cur = { ...this.cur, hinted: true }; this.persist(); } }
  subscribe(f: (p: KeyPreset) => void): () => void { this.subs.add(f); return () => { this.subs.delete(f); }; }
  private persist(): void {
    let area: KeyArea | null = null;
    try { area = typeof localStorage !== 'undefined' ? localStorage : null; } catch { area = null; }
    saveKeymap(area, this.cur);
  }
}
export const keymap = new KeymapState();
